import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, ArrowRight, Sparkles } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { brand, logoSrc } = useBrand();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(identifier, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-background">
      {/* Left brand panel */}
      <div className="hidden md:flex flex-col justify-between p-12 relative overflow-hidden"
           style={{ background: `radial-gradient(1200px circle at 15% 20%, ${brand.primary_color || "#21095b"}, transparent 55%), linear-gradient(180deg, hsl(240 15% 6%), hsl(240 15% 3%))` }}>
        <div className="text-white flex items-center gap-3">
          {logoSrc ? (
            <img src={logoSrc} alt={brand.app_name} className="h-12 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <>
              <div className="h-10 w-10 rounded-xl flex items-center justify-center font-black text-white text-lg"
                   style={{ background: `linear-gradient(135deg, ${brand.primary_color || "#21095b"}, var(--brand-color-light, #FFA35C))` }}>
                {(brand.app_name?.[0] || "C").toUpperCase()}
              </div>
              <span className="font-bold text-xl tracking-tight">{brand.app_name || "CRM Suite"}</span>
            </>
          )}
        </div>
        <div className="text-white space-y-5 relative z-10">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs uppercase tracking-widest text-white/70">
            <Sparkles className="h-3.5 w-3.5" /> Enterprise CRM
          </div>
          <h1 className="text-5xl font-bold tracking-tight leading-[1.03]">
            {brand.login_headline || (
              <>
                Multi-business CRM,<br />
                <span style={{ color: "var(--brand-color-light, #FFA35C)" }}>real-time chat</span>,<br />
                and instant lead capture.
              </>
            )}
          </h1>
          <p className="text-white/70 text-lg max-w-md">
            {brand.login_subtext || brand.tagline || "Capture Facebook leads in real-time, chat live with clients, book Meet/Zoom calls, and manage your whole sales pipeline — from any device."}
          </p>
          <div className="flex gap-6 pt-4">
            <div>
              <div className="text-3xl font-bold text-white">Live</div>
              <div className="text-xs uppercase tracking-widest text-white/50">FB Lead sync</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">WebSocket</div>
              <div className="text-xs uppercase tracking-widest text-white/50">Chat + typing</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white">PWA</div>
              <div className="text-xs uppercase tracking-widest text-white/50">Install on phone</div>
            </div>
          </div>
        </div>
        <div className="text-white/40 text-sm">{brand.footer_text || "© 2026 CRM Suite. Enterprise-grade."}</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <Card className="w-full max-w-md p-8">
          <div className="mb-8">
            <div className="text-xs font-semibold uppercase tracking-[0.15em]" style={{ color: "hsl(var(--accent))" }}>Staff Login</div>
            <h2 className="text-3xl font-bold mt-2">Sign in</h2>
            <p className="text-muted-foreground mt-1 text-sm">Use username or email</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="identifier">Username or Email</Label>
              <Input id="identifier" required value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                data-testid="login-email-input" placeholder="Enter your username or email"
                className="mt-1 h-11" autoComplete="username" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input" placeholder="••••••••"
                className="mt-1 h-11" autoComplete="current-password" />
            </div>
            <Button type="submit" disabled={loading}
              className="w-full h-11 mt-2 bg-accent text-accent-foreground hover:opacity-90"
              data-testid="login-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> :
                <span className="flex items-center gap-2">Sign in <ArrowRight className="h-4 w-4" /></span>}
            </Button>
          </form>
          <div className="mt-4 text-center">
            <span className="text-xs text-muted-foreground">Staff access only. Clients: use the portal link shared with you.</span>
          </div>
        </Card>
      </div>
    </div>
  );
}
